/**
 * The deploy's security headers, as data. vite.config.ts renders them into
 * dist/_headers. A Worker put in front (wrangler.jsonc `main`) must set
 * `securityHeaders(SECURITY_POLICY)` on every response itself: Cloudflare
 * does not apply `_headers` to a Worker's responses.
 */

export type CspDirective =
  | "default-src"
  | "script-src"
  | "style-src"
  | "img-src"
  | "font-src"
  | "connect-src"
  | "media-src"
  | "frame-src"
  | "worker-src"
  | "manifest-src"
  | "object-src"
  | "base-uri"
  | "form-action"
  | "frame-ancestors"
  | "require-trusted-types-for"
  | "trusted-types"
  | "upgrade-insecure-requests"
  | "report-uri"
  | "report-to";

const FETCH_DIRECTIVES: ReadonlySet<CspDirective> = new Set([
  "script-src",
  "style-src",
  "img-src",
  "font-src",
  "connect-src",
  "media-src",
  "frame-src",
  "worker-src",
  "manifest-src",
  "object-src",
]);

export type Csp = Readonly<Partial<Record<CspDirective, readonly string[]>>>;

export interface SecurityPolicy {
  readonly csp: Csp;
  /** Sends the CSP as Content-Security-Policy-Report-Only: violations are reported, not blocked. */
  readonly reportOnly: boolean;
  readonly headers: Readonly<Record<string, string>>;
}

export interface PolicyOverrides {
  /** Sources added to each directive; `null` drops the directive. */
  readonly csp?: Readonly<
    Partial<Record<CspDirective, readonly string[] | null>>
  >;
  readonly reportOnly?: boolean;
  /** Headers set or replaced; `null` drops the header. */
  readonly headers?: Readonly<Record<string, string | null>>;
}

/** The one Trusted Types policy the app creates, for registering sw.js. */
export const SERVICE_WORKER_POLICY = "service-worker";

export const DEFAULT_POLICY: SecurityPolicy = {
  csp: {
    "default-src": ["'self'"],
    "script-src": ["'self'"],
    "style-src": ["'self'"],
    "img-src": ["'self'", "data:"],
    "font-src": ["'self'"],
    "connect-src": ["'self'"],
    "worker-src": ["'self'"],
    "manifest-src": ["'self'"],
    "object-src": ["'none'"],
    "base-uri": ["'self'"],
    "form-action": ["'self'"],
    "frame-ancestors": ["'none'"],
    "require-trusted-types-for": ["'script'"],
    "trusted-types": [SERVICE_WORKER_POLICY],
  },
  reportOnly: false,
  headers: {
    "X-Content-Type-Options": "nosniff",
    "Referrer-Policy": "strict-origin-when-cross-origin",
    "Cross-Origin-Opener-Policy": "same-origin",
    "Cross-Origin-Resource-Policy": "same-origin",
    "Strict-Transport-Security": "max-age=31536000",
    "Permissions-Policy":
      "accelerometer=(), camera=(), geolocation=(), gyroscope=(), magnetometer=(), microphone=(), payment=(), usb=()",
  },
};

/** This app's policy. Change it through these overrides, not DEFAULT_POLICY. */
export const SECURITY_POLICY: SecurityPolicy = policyForBuild({});

export function policyForBuild(
  env: Readonly<Record<string, string | undefined>>,
): SecurityPolicy {
  const connect: string[] = [];
  const images: string[] = [];
  const convex = env.VITE_CONVEX_URL;
  if (convex) {
    const url = new URL(convex);
    connect.push(url.origin);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    connect.push(url.origin);
  }
  const corpus = env.VITE_CORPUS_BASE;
  if (corpus && /^https?:\/\//.test(corpus)) {
    const origin = new URL(corpus).origin;
    connect.push(origin);
    images.push(origin);
  }
  return mergePolicy(DEFAULT_POLICY, {
    csp: {
      // Radix positions its popovers with element style attributes.
      "style-src": ["'unsafe-inline'"],
      "connect-src": connect,
      "img-src": images,
      // The diff viewer writes HTML into its shadow root without TrustedHTML.
      "require-trusted-types-for": null,
    },
    headers: {
      "Content-Security-Policy-Report-Only":
        "require-trusted-types-for 'script'",
    },
  });
}

// Cloudflare's per-line limit in _headers, name and indentation included.
export const HEADERS_LINE_LIMIT = 2000;

export function mergePolicy(
  base: SecurityPolicy,
  overrides: PolicyOverrides,
): SecurityPolicy {
  const csp: Partial<Record<CspDirective, readonly string[]>> = {
    ...base.csp,
  };
  for (const [name, extra] of entries(overrides.csp ?? {})) {
    if (extra === null) {
      delete csp[name];
      continue;
    }
    // A fetch directive the base leaves out falls back to default-src, so
    // extending it starts from there; 'none' cannot be combined with sources.
    const current =
      csp[name] ?? (FETCH_DIRECTIVES.has(name) ? csp["default-src"] : []) ?? [];
    const kept = current.filter((source) => source !== "'none'");
    csp[name] = [...new Set([...kept, ...extra])];
  }

  const headers: Record<string, string> = {};
  const replaced = new Set(
    Object.keys(overrides.headers ?? {}).map((name) => name.toLowerCase()),
  );
  for (const [name, value] of Object.entries(base.headers)) {
    if (!replaced.has(name.toLowerCase())) headers[name] = value;
  }
  for (const [name, value] of Object.entries(overrides.headers ?? {})) {
    if (value !== null) headers[name] = value;
  }

  return {
    csp,
    reportOnly: overrides.reportOnly ?? base.reportOnly,
    headers,
  };
}

export function renderCsp(csp: Csp): string {
  return entries(csp)
    .map(([name, sources]) => [name, ...sources].join(" "))
    .join("; ");
}

export function securityHeaders(
  policy: SecurityPolicy,
): Record<string, string> {
  const cspHeader = policy.reportOnly
    ? "Content-Security-Policy-Report-Only"
    : "Content-Security-Policy";
  return { [cspHeader]: renderCsp(policy.csp), ...policy.headers };
}

/** A `_headers` file applying the policy to every path. */
export function renderHeadersFile(policy: SecurityPolicy): string {
  const lines = Object.entries(securityHeaders(policy)).map(
    ([name, value]) => `  ${name}: ${value}`,
  );
  for (const line of lines) {
    if (line.length > HEADERS_LINE_LIMIT) {
      throw new Error(
        `_headers line is ${line.length} characters, over Cloudflare's ${HEADERS_LINE_LIMIT}: ${line.slice(0, 80)}…`,
      );
    }
  }
  return ["/*", ...lines, ""].join("\n");
}

function entries<K extends string, V>(
  record: Readonly<Partial<Record<K, V>>>,
): [K, V][] {
  return Object.entries(record) as [K, V][];
}
